import React, { useMemo, useState } from 'react'
import { AssessmentCostsStyles } from './Style'
import AssessmentFiltersHeader from 'pages/School/Assessments/AssessmentFiltersHeader'
import { CircularProgress, SvgIcon } from '@mui/material'
import { SearchOutlined } from '@mui/icons-material'
import { assessmentCostsHooks } from '.'
import { decimalToTime, formatMinutesDuration } from 'utils/formatter'

const BASE_DESCRIPTION = "מוצגים נתונים של כל המבדקים שסימון המילים בהם הסתיים"

const AssessmentCosts = () => {
    // draftFilters: מה שמוצג/נערך בשורת הסינון. appliedFilters: מה שבאמת נשלף ומוצג בטבלה -
    // מתעדכן רק בלחיצה על "מיון תוצאות", כדי שבחירה בדרופדאון לא תשנה תוצאות עד לאישור מפורש
    const [draftFilters, setDraftFilters] = useState({})
    const [appliedFilters, setAppliedFilters] = useState({})

    const { categories, monthOptions, totalCount, totalAvgCost, totalAvgDurationMinutes, isLoading, totalCost, totalDurationMinutes } = assessmentCostsHooks.useAssessmentCostsData({ assessmentFilters: appliedFilters })
    const filterItems = assessmentCostsHooks.useFilterItems({ monthOptions })

    // בונה את שורת התיאור באופן דינמי לפי הסינון שבאמת הוחל (לא לפי מה שנערך כרגע בדרופדאונים)
    const description = useMemo(() => {
        const parts = [BASE_DESCRIPTION]

        const categoryLabels = appliedFilters.categories?.map(c => c.label || c.name).filter(Boolean).join(', ')
        if (categoryLabels)
            parts.push(`מסוג ${categoryLabels}`)

        const monthLabels = appliedFilters.marking_end_months?.map(m => m.name).filter(Boolean).join(', ')
        if (monthLabels)
            parts.push(`שסומנו ב${monthLabels}`)

        const transcriberName = appliedFilters.marking_words_update_by?.name || appliedFilters.marking_words_update_by?.full_name
        if (transcriberName)
            parts.push(`על ידי המתעתקת ${transcriberName}`)

        return parts.join(' ')
    }, [appliedFilters])

    return (
        <AssessmentCostsStyles>
            <AssessmentFiltersHeader
                introLabel={"סינון לפי:"}
                description={description}
                rowHeight={44}
                keys={['category', 'assessment_def']}
                searchLabel={"מיון תוצאות"}
                searchIcon={<SearchOutlined fontSize="small" />}
                searchButtonProps={{ variant: undefined, color: "primary500", size: "small", height: undefined, width: undefined }}
                refetchData={() => setAppliedFilters(draftFilters)}
                headerItems={filterItems}
                loading={isLoading}
                showLoading={true}
                assessmentFilters={draftFilters}
                setAssessmentFilters={setDraftFilters}
            />
            {!isLoading && categories.length > 0 &&
                <div className='top-card'>
                    <div className='stats-row'>
                        <div className='stat-tile'>
                            <div className='stat-label'>{"כמות מבדקים שנבדקו"}</div>
                            <div className='stat-value'>{`${totalCount} מבדקים`}</div>
                        </div>
                        <div className='stat-tile'>
                            <div className='stat-label'>{"עלות כוללת"}</div>
                            <div className='stat-value'>{`₪${totalCost?.toFixed(1)}`}</div>
                        </div>
                        <div className='stat-tile'>
                            <div className='stat-label'>{"עלות סופית כוללת"}</div>
                            <div className='stat-value'>{`₪${(totalCost * 1.5)?.toFixed(1)}`}</div>
                        </div>
                        <div className='stat-tile'>
                            <div className='stat-label'>{"זמן בדיקה כולל"}</div>
                            <div className='stat-value'>{formatMinutesDuration(totalDurationMinutes)}</div>
                        </div>

                        <div className='stat-tile'>
                            <div className='stat-label'>{"זמן בדיקה כולל בממוצע"}</div>
                            <div className='stat-value'>{formatMinutesDuration(totalAvgDurationMinutes)}</div>
                        </div>
                        <div className='stat-tile'>
                            <div className='stat-label'>{"עלות כוללת בממוצע"}</div>
                            <div className='stat-value'>{`₪${totalAvgCost?.toFixed(1)}`}</div>
                        </div>
                        <div className='stat-tile'>
                            <div className='stat-label'>{"עלות סופית בממוצע"}</div>
                            <div className='stat-value'>{`₪${(totalAvgCost * 1.5)?.toFixed(1)}`}</div>
                        </div>
                    </div>
                </div>
            }
            {isLoading ?
                <div className='loading-container'>
                    <CircularProgress color="thirdly" />
                </div>
                : categories.length ?
                    <div className='comparison-section'>
                        <div className='comparison-title'>{"בהשוואה בין קטגוריות"}</div>
                        <table className='costs-table'>
                            <thead>
                                <tr>
                                    <th>{"קטגוריה"}</th>
                                    <th>{"כמות מבדקים"}</th>
                                    <th>{"עלות כוללת"}</th>
                                    <th>{"עלות סופית כוללת"}</th>
                                    <th>{"משך סימון כולל"}</th>
                                    <th>{"משך סימון ממוצע למבדק"}</th>
                                    <th>{"עלות ממוצעת למבדק"}</th>
                                    <th>{"עלות סופית ממוצעת למבדק"}</th>

                                </tr>
                            </thead>
                            <tbody>
                                {categories.map(category => (
                                    <tr key={category.categoryName}>
                                         {/*קטגוריה */}
                                        <td>
                                            <div className='category-cell'>
                                                {category.icon &&
                                                    <SvgIcon className='category-icon' component={category.icon} inheritViewBox />}
                                                {category.label}
                                            </div>
                                        </td>
                                        <td>{category.count}</td>
                                        {/* עלות כוללת */}
                                        <td>{`₪${category.totalCost?.toFixed(1)}`}</td>
                                        {/* עלות סופית כוללת */}
                                        <td>{`₪${(category.totalCost * 1.5)?.toFixed(1)}`}</td>
                                         {/*משך סימון כולל*/}
                                         <td>{formatMinutesDuration(category.totalDurationMinutes)}</td>
                                        {/*משך סימון ממוצע למבדק*/}
                                        <td>{`${decimalToTime(category.avgDurationMinutes)} דקות`}</td>
                                        {/* עלות ממוצעת למבדק*/}
                                        <td>{`₪${category.avgCost.toFixed(1)}`}</td>
                                        {/* עלות סופית ממוצעת למבדק*/}
                                        <td>{`₪${(category.avgCost * 1.5).toFixed(1)}`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    :
                    <div className='empty-state'>{"אין תוצאות לסינון זה"}</div>
            }
        </AssessmentCostsStyles>
    )
}

export default AssessmentCosts
